"use client";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../third-party/shadcn/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "../../../../third-party/shadcn/chart";
import { useEffect, useState } from "react";
import {
  chartColors,
  getChartColor,
} from "../../../../constants/design.constants";

const chartConfig = {
  runTime: {
    label: "runTime",
  },
} satisfies ChartConfig;

function generateSequentialArray(): number[] {
  const today = new Date();
  const dayOfMonth = today.getUTCDate();
  return Array.from({ length: dayOfMonth }, (_, i) => i + 1);
}

function parseDay(dateString: string): number {
  const date = new Date(dateString);
  return date.getUTCDate();
}

function calculateAverage(arr: number[]): number {
  const sum = arr.reduce((a, b) => a + b, 0);
  return sum / arr.length;
}

function transformData(items: any[]): any[] {
  const groupedData: { [key: string]: { [key: number]: number[] } } = {};

  for (const item of items) {
    const day = parseDay(item.lastRunDate);

    if (!groupedData[item.friendlyName]) {
      groupedData[item.friendlyName] = { [day]: [item.duration] };
    } else if (!groupedData[item.friendlyName][day]) {
      groupedData[item.friendlyName][day] = [item.duration];
    } else {
      groupedData[item.friendlyName][day].push(item.duration);
    }
  }

  const result = [];
  const today = new Date().getUTCDate();
  let colorIndex = 0;

  for (const friendlyName in groupedData) {
    const data = [];

    for (let day = 1; day <= today; day++) {
      if (groupedData[friendlyName][day]) {
        const averageTime = calculateAverage(groupedData[friendlyName][day]);
        data.push(averageTime);
      } else {
        data.push(0);
      }
    }

    const color = getChartColor(colorIndex++);
    result.push({
      label: friendlyName,
      data,
      borderColor: color,
      backgroundColor: color,
    });
  }

  return result;
}

interface ChartDataPoint {
  day: string;
  [key: string]: number | string;
}

export function RunTime(props) {
  const { data } = props;
  const [chartProps, setChartProps] = useState<ChartDataPoint[]>([]);
  const [seriesMeta, setSeriesMeta] = useState<
    { label: string; borderColor: string }[]
  >([]);

  const init = () => {
    if (!data?.length) {
      return;
    }

    const labels = generateSequentialArray();
    const transformedData = transformData(data);
    setSeriesMeta(
      transformedData.map((d) => ({
        label: d.label,
        borderColor: d.borderColor,
      }))
    );

    const chartData = labels.map((label, index) => {
      const dataPoint: ChartDataPoint = { day: label.toString() };
      transformedData.forEach((dataset) => {
        dataPoint[dataset.label] = dataset.data[index];
      });
      return dataPoint;
    });

    setChartProps(chartData);
  };

  useEffect(() => {
    init();
  }, [data]);

  const colorForKey = (key: string) =>
    seriesMeta.find((s) => s.label === key)?.borderColor || chartColors.primary;

  return (
    <Card className="!shadow-none !border-none h-full bg-transparent">
      <CardHeader className="flex flex-col items-stretch space-y-0 p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-5 py-5">
          <CardTitle>Run Time</CardTitle>
          <CardDescription>
            Daily run time —{" "}
            {new Date().toLocaleString("default", { month: "long" })}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:pb-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[410px] w-full"
        >
          <LineChart
            accessibilityLayer
            data={chartProps}
            margin={{
              left: 12,
              right: 12,
              top: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid
              vertical={false}
              stroke={chartColors.grid}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={{ stroke: chartColors.grid }}
              tickMargin={8}
              minTickGap={32}
              tick={{ fill: chartColors.axis, fontSize: 11 }}
              label={{
                value: "Day of Month",
                position: "bottom",
                offset: 0,
                style: { fill: chartColors.axis, fontSize: 11 },
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={{ stroke: chartColors.grid }}
              tick={{ fill: chartColors.axis, fontSize: 11 }}
              label={{
                value: "time (s)",
                angle: -90,
                position: "insideLeft",
                style: { fill: chartColors.axis, fontSize: 11 },
              }}
            />
            <ChartTooltip
              content={<ChartTooltipContent className="w-[200px]" />}
            />
            <ChartLegend verticalAlign="top" height={36} />
            {chartProps.length > 0 &&
              Object.keys(chartProps[0])
                .filter((key) => key !== "day")
                .map((key) => (
                  <Line
                    key={key}
                    dataKey={key}
                    type="linear"
                    stroke={colorForKey(key)}
                    strokeWidth={1.5}
                    fill={colorForKey(key)}
                    dot={false}
                    activeDot={{ r: 3, strokeWidth: 0 }}
                  />
                ))}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
